// ============= GENERAL DASHBOARD DATA (Layer 1) =============
// Fetches the standing dashboard: one POST to the `dashboard-data` edge fn →
// one complete filled DashboardSpec (plain JSON, no SSE). Deliberately bypasses
// normalizePartial/isRenderableSection — those guarded a hallucinating LLM
// mid-stream and silently drop the new tier_bar/store_progress kinds; this
// response is our own deterministic server output, so the only sanity check is
// that `sections` is an array (SectionErrorBoundary contains the rest).

import { useQuery } from "@tanstack/react-query";
import { getFirebaseIdToken } from "@/shared/integrations/firebase/client";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import {
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
} from "@/shared/integrations/supabase/client";
import type { DashboardSpec } from "../components/types";

const ENDPOINT = "dashboard-data";

export function useDashboardData(clientId: string | null) {
  return useQuery({
    queryKey: ["dashboard-data", clientId],
    enabled: !!clientId,
    // Hold across tab switches for ~5 min (useIntelGate convention); the
    // Refresh affordance calls refetch() for an on-demand refill.
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<DashboardSpec> => {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Please sign in to view the dashboard.");

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/${ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ clientId }),
      });

      if (!resp.ok) {
        captureEdgeFailure({ endpoint: ENDPOINT, status: resp.status });
        throw new Error(`Dashboard data request failed (HTTP ${resp.status})`);
      }

      const spec = (await resp.json()) as DashboardSpec;
      if (!Array.isArray(spec?.sections)) {
        throw new Error("Malformed dashboard response");
      }
      return spec;
    },
  });
}
